import React, {forwardRef} from 'react';
import {useFormContext} from 'react-hook-form';
import {Markdown} from '../../markdown';
import {
    InputElement,
    InputContainer,
    InputAdornmentElement,
    TooltipElement,
    PopperElement,
    HelpIconElement
} from './Input.styles';

export interface IInputProps {
    name?: string;
    label?: string;
    type?: string;
    validation?: string;
    width?: '25%' | '50%' | '75%' | '100%';
    maxLength?: number;
    fieldName: string;
    tooltip?: string;
    tooltipLink?: string;
    defaultValue?: string;
    disabled?: boolean;
    value?: string;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

const renderTooltip = (title?: string, link?: string) => {
    if (!title) {
        return null;
    }
    const onClick = (link) ? () => {
        window.open(link, '_blank')
    } : () => {
    };
    return (
        <InputAdornmentElement position="end" className='help-icon'>
            <TooltipElement title={title} PopperComponent={PopperElement}>
                <HelpIconElement onClick={onClick}/>
            </TooltipElement>
        </InputAdornmentElement>
    )
}


const Input = forwardRef<HTMLInputElement, IInputProps>(
    ({type, label, validation, width, fieldName, maxLength, tooltip, tooltipLink, defaultValue, disabled, value, onChange, onBlur, name}, ref) => {
        const {setValue} = useFormContext();

        const isControlled = value !== undefined;
        const inputValue = isControlled ? value : defaultValue;
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (onChange) {
                onChange(e); // Use provided onChange for controlled mode
            } else {
                setValue(fieldName, e.target.value); // Fall back to form context
            }
        };
        return (
            <InputContainer width={width}>
                <InputElement
                    label={label}
                    value={isControlled ? inputValue : undefined}
                    defaultValue={!isControlled ? defaultValue : undefined}
                    disabled={disabled ?? false}
                    variant="outlined"
                    inputProps={{maxLength: maxLength}}
                    type={type}
                    error={!!validation}
                    inputRef={ref}
                    onChange={handleChange}
                    onBlur={onBlur}
                    onInput={(e) => {
                        const target = e.target as HTMLInputElement;
                        let value = target.value;
                        if (type === 'tel') {
                            // Remove all invalid characters
                            value = value.replace(/[^0-9+]/g, '');

                            // Ensure only one plus sign at the beginning
                            if (value.includes('+')) {
                                value = '+' + value.replace(/\+/g, '');
                            }

                            target.value = value;
                        }
                        else if (type === 'alphanumeric') {
                            // Only allow letters and numbers
                            value = value.replace(/[^a-zA-Z0-9]/g, '');
                            target.value = value;
                        }
                    }}
                    onKeyPress={(e) => {
                        if (!/[0-9+]/.test(e.key) && type === 'tel') {
                            e.preventDefault();
                        }
                    }}
                    InputProps={{
                        endAdornment: renderTooltip(tooltip, tooltipLink)
                    }}
                />
                {validation && (
                    <Markdown children={validation} className="validationText"/>
                )}
            </InputContainer>
        );
    }
);
export default Input;
